import React from 'react';
import { Progress, Card, Typography, Space, Button, Tag } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { JOB_COMPLETED, JOB_FAILED, JOB_PENDING, JOB_PROCESSING } from 'constants/index';

const { Text } = Typography;

const ImportProgressCard = ({
    job,
    onRetry,
    onCancel,
    onClear,
    showDetails = true
}) => {
    const { t } = useTranslation();

    if (!job) return null;

    const { status, progress, type, error, createdAt } = job;

    const getStatusIcon = () => {
        switch (status) {
            case JOB_PENDING:
                return <ClockCircleOutlined style={{ color: '#faad14' }} />;
            case JOB_PROCESSING:
                return <LoadingOutlined style={{ color: '#1890ff' }} />;
            case JOB_COMPLETED:
                return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
            case JOB_FAILED:
                return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
            default:
                return null;
        }
    };

    const getStatusColor = () => {
        switch (status) {
            case JOB_PENDING:
                return 'orange';
            case JOB_PROCESSING:
                return 'blue';
            case JOB_COMPLETED:
                return 'green';
            case JOB_FAILED:
                return 'red';
            default:
                return 'default';
        }
    };

    const getProgressPercent = () => {
        if (!progress) return 0;
        return progress.percentage || 0;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleString();
    };

    return (
        <Card
            size="small"
            style={{ marginBottom: 16 }}
            title={
                <Space>
                    {getStatusIcon()}
                    <Text strong>
                        {t('import')} {type === 'shop' ? t('shop') : t('cabinet')}
                    </Text>
                    <Tag color={getStatusColor()}>{t(status)}</Tag>
                </Space>
            }
            extra={
                <Space>
                    {status === JOB_FAILED && onRetry && (
                        <Button size="small" type="primary" onClick={onRetry}>
                            {t('retry')}
                        </Button>
                    )}
                    {status === JOB_PROCESSING && onCancel && (
                        <Button size="small" danger onClick={onCancel}>
                            {t('cancel')}
                        </Button>
                    )}
                    {(status === JOB_COMPLETED || status === JOB_FAILED) && onClear && (
                        <Button size="small" onClick={onClear}>
                            {t('clear')}
                        </Button>
                    )}
                </Space>
            }
        >
            <Space direction="vertical" style={{ width: '100%' }}>
                {status === JOB_PROCESSING && (
                    <Progress
                        percent={getProgressPercent()}
                        size="small"
                        status={status === JOB_FAILED ? 'exception' : 'active'}
                    />
                )}

                {showDetails && progress && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        <Space split={<span>•</span>}>
                            <span>{t('total')}: {progress.total || 0}</span>
                            <span>{t('processed')}: {progress.processed || 0}</span>
                            <span style={{ color: '#52c41a' }}>{t('success')}: {progress.success || 0}</span>
                            {progress.failed > 0 && (
                                <span style={{ color: '#ff4d4f' }}>{t('failed')}: {progress.failed}</span>
                            )}
                        </Space>
                    </div>
                )}

                {error && (
                    <Text type="danger" style={{ fontSize: '12px' }}>
                        {t('error')}: {error}
                    </Text>
                )}

                <Text type="secondary" style={{ fontSize: '11px' }}>
                    {t('startedAt')}: {formatDateTime(createdAt)}
                </Text>
            </Space>
        </Card>
    );
};

export default ImportProgressCard;
